import React, { useEffect, useRef, useMemo, forwardRef, useState } from 'react'
import { AbsoluteFill, Img, OffthreadVideo, staticFile, useCurrentFrame, useVideoConfig, Video, Freeze, getRemotionEnvironment } from 'remotion'
import { applyAnimations, calculateTrimTimes, prefetchAsset } from '../../../shared/utils/remotion'
import { Gif } from '@remotion/gif'
import CtaOverlay from './CtaOverlay'
import ProductIntroOverlay from './ProductIntroOverlay'

interface Animation {
  delay: number
  duration: number
  enabled: boolean
  type: string
  name: string
  data: any
}

interface Child {
  name: any
  custom: any
  id: string
  type: string
  text?: string
  animations: Animation[]
  src?: string
  clipSrc?: string
  x: number
  y: number
  width: number
  height: number
  rotation: number
  fontFamily?: string
  fontSize?: number
  fontWeight?: string
  fontStyle?: string
  textAlign?: string
  fill?: string
  cornerRadius?: number
  borderSize?: number
  cropX: number
  cropY: number
  blurEnabled: boolean
  blurRadius: number
  sepiaEnabled: boolean
  grayscaleEnabled: boolean
  brightnessEnabled: boolean
  brightness: number
  shadowEnabled: boolean
  shadowBlur: number
  letterSpacing: number
  strokeWidth: number
  stroke: string
  lineHeight: number
  cropWidth: number
  cropHeight: number
  duration: number
  startTime: number
  endTime: number
  opacity?: number
  visible?: boolean
  borderColor?: string
  shadowOffsetX?: number
  shadowOffsetY?: number
  shadowColor?: string
  shadowOpacity?: number
  flipX?: boolean
  flipY?: boolean
  filters?: any
}

interface PageData {
  id: string
  children: Child[]
}

interface SinglePageProps {
  pageHtml: any
  width: number
  height: number
  pageData: PageData
  isDownload: boolean
  aiAvatar: boolean
  overlayAnimation: boolean
  webScroll: boolean
  premountFor?: number
  sectionLength?: number
  ctaData?: any,
  sectionIndex?: number,
  productIntroData?: {
    image: string;
    headline: string;
    subheadline?: string;
    templateKey: 1 | 2 | 3 | 4 | 5 | 6;
    productAnimation?: 'fadeIn' | 'slideIn' | 'zoomIn' | 'rotateIn' | 'bounceIn' | 'splitReveal';
  }
}

// Create a custom forwarded ref video component since OffthreadVideo doesn't support refs directly
const ForwardedVideo = forwardRef<HTMLDivElement, {
  src: string;
  style: React.CSSProperties;
  startFrom?: number;
  muted?: boolean;
  transparent?: boolean;
}>((props, ref) => {
  return (
    <div ref={ref} className="video-wrapper" style={{ width: '100%', height: '100%' }}>
      <OffthreadVideo
        {...props}
      />
    </div>
  );
});

ForwardedVideo.displayName = 'ForwardedVideo';

// Update the PremountedMedia component
const PremountedMedia: React.FC<{
  src: string
  type: 'video' | 'image'
  style: React.CSSProperties
  startFrom?: number
  muted?: boolean
  transparent?: boolean
  premountFor: number
  brightness?: number
  brightnessEnabled?: boolean
  id?: string
  onMediaRef?: (id: string, ref: HTMLElement | null, type: 'video' | 'image') => void
}> = ({ src, type, style, startFrom, muted, transparent, premountFor, brightness, brightnessEnabled, id, onMediaRef }) => {
  const frame = useCurrentFrame();
  const isPremounting = frame < 0 && frame >= -premountFor;
  const imgRef = useRef<HTMLImageElement>(null);
  const videoRef = useRef<HTMLDivElement>(null);
  const [isMediaLoaded, setIsMediaLoaded] = useState(false);
  
  // Apply brightness directly to element style if enabled
  const computedStyle = { ...style };
  if (brightnessEnabled && brightness !== undefined) {
    const normalizedBrightness = brightness / 100;
    computedStyle.filter = computedStyle.filter 
      ? `${computedStyle.filter} brightness(${normalizedBrightness})` 
      : `brightness(${normalizedBrightness})`;
  }

  // Effect for handling refs for both video and image elements
  useEffect(() => {
    if (!id || !onMediaRef) return;
    
    if (type === 'video' && videoRef.current) {
      // Using MutationObserver to detect when the video element is added to the DOM
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            const videoElement = videoRef.current?.querySelector('video');
            if (videoElement) {
              onMediaRef(id, videoElement as HTMLVideoElement, 'video');
              setIsMediaLoaded(true);
              observer.disconnect();
              break;
            }
          }
        }
      });
      
      observer.observe(videoRef.current, { childList: true, subtree: true });
      
      // Immediate check in case the video is already there
      const videoElement = videoRef.current.querySelector('video');
      if (videoElement) {
        onMediaRef(id, videoElement as HTMLVideoElement, 'video');
        setIsMediaLoaded(true);
        observer.disconnect();
      }
      
      return () => {
        observer.disconnect();
      };
    } else if (type === 'image' && imgRef.current) {
      onMediaRef(id, imgRef.current, 'image');
      setIsMediaLoaded(true);
    }
  }, [id, onMediaRef, type]);

  // Handle image load event
  const handleImageLoad = () => {
    if (id && onMediaRef && imgRef.current) {
      onMediaRef(id, imgRef.current, 'image');
      setIsMediaLoaded(true);
    }
  };

  if (type === 'video') {
    return (
      <ForwardedVideo
        ref={videoRef}
        src={src}
        style={computedStyle}
        startFrom={startFrom}
        muted={muted}
        transparent={transparent}
      />
    );
  } else {
    return (
      <Img 
        ref={imgRef} 
        src={src} 
        style={computedStyle} 
        onLoad={handleImageLoad}
      />
    );
  }
};

const createMediaStyle = (child: Child, webScroll: boolean, scaleFactor: number, frame: number, durationInFrames: number) => {
  // If this is a web scroll element (except for logos), we need to handle it specially
  const isWebScrollElement = webScroll && child.type === 'image' && !child.name?.includes('logo');
  
  const mediaStyle: React.CSSProperties = {
    position: 'absolute',
    width: isWebScrollElement ? '100%' : `${(1 / child.cropWidth) * 100}%`,
    height: isWebScrollElement ? 'auto' : `${(1 / child.cropHeight) * 100}%`,
    objectFit: isWebScrollElement ? 'contain' : 'cover',
    objectPosition: isWebScrollElement ? 'top' : 'center',
    transform: isWebScrollElement
      ? `translateX(-${child.cropX * 100}%) translateY(${
          ((child.height * scaleFactor) * frame) / durationInFrames * -1
        }px)`
      : `translate(-${child.cropX * 100}%, -${child.cropY * 100}%)`,
    maxWidth: isWebScrollElement ? '100%' : 'none',
    opacity: child.opacity !== undefined ? child.opacity : 1,
    display: child.visible === false ? 'none' : 'block',
    borderRadius: `${child.cornerRadius || 0}px`,
    border: child.borderSize && child.borderSize > 0 
      ? `${child.borderSize}px solid ${child.borderColor || 'black'}` 
      : 'none',
    // For web scroll elements, we need transform-origin to be top center
    transformOrigin: isWebScrollElement ? 'top center' : 'center center',
  };

  // If this is a web scroll element, we return early to avoid applying other transformations
  if (isWebScrollElement) {
    return mediaStyle;
  }

  // Apply visual filters
  const filters = [];
  
  // Apply blur filter if enabled
  if (child.blurEnabled) {
    // Apply a standard blur effect without any oval shaping
    filters.push(`blur(${child.blurRadius || 0}px)`);
  }
  
  // Apply brightness filter if enabled
  if (child.brightnessEnabled && child.brightness !== undefined) {
   
    // Normalize brightness from 0-200 range to CSS brightness filter range
    // In CSS brightness filter: 0 = black, 1 = normal, >1 = brighter
    // For 0-200 range: 0 = black, 100 = normal, 200 = very bright
    const normalizedBrightness = child.brightness / 100;
   
    filters.push(`brightness(${normalizedBrightness})`);
  }
  
  // Apply sepia filter if enabled
  if (child.sepiaEnabled) {
    filters.push('sepia(1)');
  }
  
  // Apply grayscale filter if enabled
  if (child.grayscaleEnabled) {
    filters.push('grayscale(1)');
  }
  
  // Apply filters from the filters object (like Natural, Warm, Cold)
  if (child.filters) {
    if (child.filters.warm && child.filters.warm.intensity > 0) {
      filters.push(`sepia(${child.filters.warm.intensity})`);
    }
    if (child.filters.cold && child.filters.cold.intensity > 0) {
      // Cold is typically a blue tint
      filters.push(`hue-rotate(180deg) saturate(${1 + child.filters.cold.intensity})`);
    }
  }
  
  // Apply shadow if enabled
  if (child.shadowEnabled && child.shadowBlur) {
    // Ensure we use the exact offset values from the properties
    const shadowOffsetX = child.shadowOffsetX !== undefined ? child.shadowOffsetX : 0;
    const shadowOffsetY = child.shadowOffsetY !== undefined ? child.shadowOffsetY : 0;
    const shadowColor = child.shadowColor || 'black';
    const shadowOpacity = child.shadowOpacity !== undefined ? child.shadowOpacity : 1;
    
    // Create the shadow with RGBA color to handle opacity
    let shadowColorWithOpacity = shadowColor;
    if (shadowColor.startsWith('#')) {
      // Convert hex to rgba
      const r = parseInt(shadowColor.slice(1, 3), 16);
      const g = parseInt(shadowColor.slice(3, 5), 16);
      const b = parseInt(shadowColor.slice(5, 7), 16);
      shadowColorWithOpacity = `rgba(${r}, ${g}, ${b}, ${shadowOpacity})`;
    } else if (shadowColor === 'black') {
      shadowColorWithOpacity = `rgba(0, 0, 0, ${shadowOpacity})`;
    }

    // Use exact shadow values without any modifications or trimming
    // The drop-shadow filter preserves the element's alpha channel
    filters.push(`drop-shadow(${shadowOffsetX}px ${shadowOffsetY}px ${child.shadowBlur}px ${shadowColorWithOpacity})`);
    
  }
  
  // Apply all filters if any exist
  if (filters.length > 0) {
    mediaStyle.filter = filters.join(' ');
   
    // For brightness specifically, add it with !important to ensure it's not overridden
    if (child.brightnessEnabled && child.brightness !== undefined) {
    
      // This is a special case for browsers that might override the filter
      mediaStyle.WebkitFilter = mediaStyle.filter;
    }
  }

  // Apply mask if clipSrc is available
  if (child.clipSrc) {
    // Only apply mask if the image is not blurred
    if (!child.blurEnabled || child.blurRadius === 0) {
      // For proper mask sizing, we apply it to cover the container completely
      mediaStyle.WebkitMaskImage = `url(${child.clipSrc})`;
      mediaStyle.WebkitMaskSize = '100% 100%';  // Cover the entire element
      mediaStyle.WebkitMaskPosition = 'center';
      mediaStyle.WebkitMaskRepeat = 'no-repeat';
      
      // Standard mask properties (for non-webkit browsers)
      mediaStyle.maskImage = `url(${child.clipSrc})`;
      mediaStyle.maskSize = '100% 100%';  // Cover the entire element 
      mediaStyle.maskPosition = 'center';
      mediaStyle.maskRepeat = 'no-repeat';
    }
  }
  
  // Apply flip transformations if needed
  if (child.flipX || child.flipY) {
    const flipX = child.flipX ? 'scaleX(-1)' : '';
    const flipY = child.flipY ? 'scaleY(-1)' : '';
    const currentTransform = mediaStyle.transform as string;
    mediaStyle.transform = `${currentTransform} ${flipX} ${flipY}`.trim();
  }

  return mediaStyle;
};

export const SinglePage: React.FC<SinglePageProps> = React.memo(
  ({ pageHtml, width, height, pageData, isDownload, aiAvatar, overlayAnimation, webScroll, premountFor = 0, sectionLength, ctaData, sectionIndex, productIntroData }: SinglePageProps) => {
    const frame = useCurrentFrame()
    const { fps, durationInFrames, width: videoWidth, height: videoHeight } = useVideoConfig()
    const pageRef = useRef<HTMLDivElement>(null)
    
    // Create a ref map to store references to media elements with type information
    const mediaRefsMap = useRef<Map<string, {element: HTMLElement, type: string}>>(new Map());
    
    // Handler to store media element refs
    const handleMediaRef = (id: string, element: HTMLElement | null, type: 'video' | 'image') => {
      if (element) {
        mediaRefsMap.current.set(id, {element, type});
        
        // Debug logging to verify element is being stored properly
        // console.log(`Media element stored: ${id}, type: ${type}`, element);
      } else {
        mediaRefsMap.current.delete(id);
      }
    };

    // Calculate if we're in premount state
    const isPremounting = frame < 0 && frame >= -premountFor

    useEffect(() => {
      if (!isPremounting) {
        // Check if there are any video children - if so, skip HTML animations entirely
        const hasVideoChildren = pageData?.children?.some(child => child.type === 'video');
        
        if (!hasVideoChildren) {
          // Filter out HTML elements that should not have animations applied
          const filteredPageData = {
            ...pageData,
            children: pageData?.children?.filter(child => {
              // Skip animation for aiAvatar elements
              if (aiAvatar && (child?.custom?.aiAvatar || child?.name?.includes('avatar'))) {
                return false;
              }
              // Skip animation for web scroll elements
              if (webScroll && child.type === 'image' && !child.name?.includes('logo')) {
                return false;
              }
              return true;
            })
          };
          
          // Apply animations to HTML content only if no videos present
          applyAnimations(pageRef, filteredPageData, frame, fps, durationInFrames, width, height);
        }
        
        // Create a data structure that resembles the pageData but with media element refs
        // And filter out avatar and web scroll elements, but keep videos for media animations
        const mediaPageData = {
          children: pageData?.children
            ?.filter(child => {
              // Only include videos and images that have refs
              if ((child.type !== 'video' && child.type !== 'image') || !mediaRefsMap.current.has(child.id)) {
                return false;
              }
              
              // Skip animation for aiAvatar elements
              if (aiAvatar && (child?.custom?.aiAvatar || child?.name?.includes('avatar'))) {
                return false;
              }
              
              // Skip animation for web scroll elements
              if (webScroll && child.type === 'image' && !child.name?.includes('logo')) {
                return false;
              }
              
              return true;
            })
            .map(child => ({
              ...child,
              element: mediaRefsMap.current.get(child.id)?.element
            }))
        };
        
        // Apply animations to media elements (if any remain after filtering)
        if (mediaPageData?.children?.length > 0) {
          // Create a ref-like object that the applyAnimations function expects
          const mediaRef = {
            current: {
              querySelector: (selector: string) => {
                // Extract the ID from the selector (e.g., #element-id)
                const id = selector.substring(1);
                return mediaRefsMap.current.get(id)?.element || null;
              }
            }
          };
          
          applyAnimations(mediaRef, mediaPageData, frame, fps, durationInFrames, width, height);
        }
      }
    }, [frame, pageData, fps, durationInFrames, width, height, isPremounting, aiAvatar, webScroll]);

    useEffect(() => {
      if (isPremounting) {
        const prefetchPromises = pageData?.children?.map(async (child: any) => {
          if (child.type === 'video' || (child.type === 'image' && child.src)) {
            try {
              await prefetchAsset(child.src, child.type === 'image' ? 'image/png' : 'video/mp4')
            } catch (error) {
              console.error(`Failed to prefetch ${child.src}:`, error)
            }
          }
        })
        Promise.all(prefetchPromises).catch(console.error)
      }
    }, [pageData, isPremounting])

    const scaleFactor = isDownload ? 1 : Math.min(width / 1 / width, height / 1 / height)

    const renderMedia = (isPremounted: boolean) => (
      <AbsoluteFill style={{ position: 'relative' }}>
        {pageData?.children?.map((child, index) => {
          if ((child.type === 'video' || child.type === 'image') && child.src) {
            const isWebScrollElement = webScroll && child.type === 'image' && !child.name?.includes('logo');
            const isAvatarElement = aiAvatar && (child?.custom?.aiAvatar || child?.name?.includes('avatar'));
            
            const cropStyle: React.CSSProperties = {
              position: 'absolute',
              overflow: 'visible',
              left: isDownload ? `${child.x}px` : `${child.x * scaleFactor}px`,
              top: isDownload ? `${child.y}px` : `${child.y * scaleFactor}px`,
              width: isDownload ? `${child.width}px` : `${child.width * scaleFactor}px`,
              height: isDownload ? `${child.height}px` : `${child.height * scaleFactor}px`,
              transform: `rotate(${child.rotation}deg)`,
              padding: child.shadowEnabled ? '40px' : '0px',
              margin: child.shadowEnabled ? '-40px' : '0px',
            }

            const mediaStyle = createMediaStyle(child, webScroll, scaleFactor, frame, durationInFrames);

            // Build className based on element type
            const containerClassName = [
              child.type === 'video' ? 'video-container' : 'image-container',
              isWebScrollElement ? 'web-scroll-element' : '',
              isAvatarElement ? 'avatar-element' : ''
            ].filter(Boolean).join(' ');

            if (child.type === 'video') {
              const trimmedDuration = calculateTrimTimes(
                child.duration || child?.custom?.duration || 20000,
                child.startTime || child?.custom?.startTime || 0,
                child.endTime || child?.custom?.endTime || 1,
                fps
              )

              const isMute = !isDownload ? true : child.custom?.aiAvatar ? false : true

              return (
                <div 
                  key={child.id} 
                  style={cropStyle} 
                  id={`container-${child.id}`} 
                  data-id={child.id} 
                  className={containerClassName}
                  data-is-avatar={isAvatarElement ? 'true' : 'false'}
                  data-is-webscroll={isWebScrollElement ? 'true' : 'false'}
                >
                  <PremountedMedia
                    id={child.id}
                    onMediaRef={handleMediaRef}
                    src={child.src}
                    type="video"
                    style={mediaStyle}
                    startFrom={trimmedDuration.startTime}
                    muted={child.duration < 3500 ? true : isMute}
                    transparent={child?.custom?.bg && !isMute}
                    premountFor={premountFor}
                    brightness={child.brightness}
                    brightnessEnabled={child.brightnessEnabled}
                  />
                </div>
              )
            } else {
              return (
                <div 
                  key={child.id} 
                  style={cropStyle} 
                  id={`container-${child.id}`} 
                  data-id={child.id} 
                  className={containerClassName}
                  data-is-avatar={isAvatarElement ? 'true' : 'false'}
                  data-is-webscroll={isWebScrollElement ? 'true' : 'false'}
                >
                  <PremountedMedia
                    id={child.id}
                    onMediaRef={handleMediaRef}
                    src={child.src}
                    type="image"
                    style={mediaStyle}
                    premountFor={premountFor}
                    brightness={child.brightness}
                    brightnessEnabled={child.brightnessEnabled}
                  />
                </div>
              )
            }
          }
          return null
        })}
      </AbsoluteFill>
    );

    return (
      <>
        {isPremounting ? (
          <Freeze frame={0}>
            <AbsoluteFill 
              style={{ 
                opacity: 0,
                pointerEvents: 'none',
                willChange: 'transform, opacity'
              }}
            >
              {renderMedia(true)}

              <div
                ref={pageRef}
                style={{
                  position: 'absolute',
                  transform: isDownload ? 'none' : `scale(${scaleFactor})`,
                  transformOrigin: 'top left',
                  width: `${width}px`,
                  height: `${height}px`,
                }}
                dangerouslySetInnerHTML={{ __html: pageHtml }}
              />
              {overlayAnimation && (
                <AbsoluteFill>
                  <PremountedMedia
                    src={staticFile('remotionPartical/particle_3.webm')}
                    type="video"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    muted={true}
                    transparent={true}
                    premountFor={premountFor}
                  />
                </AbsoluteFill>
              )}
            </AbsoluteFill>
          </Freeze>
        ) : (
          <AbsoluteFill 
            style={{ 
              opacity: 1,
              pointerEvents: 'auto',
              willChange: 'transform, opacity'
            }}
          >
            {renderMedia(false)}

            <div
              ref={pageRef}
              style={{
                position: 'absolute',
                transform: isDownload ? 'none' : `scale(${scaleFactor})`,
                transformOrigin: 'top left',
                width: `${width}px`,
                height: `${height}px`,
              }}
              dangerouslySetInnerHTML={{ __html: pageHtml }}
            />
            {overlayAnimation && (
              <AbsoluteFill>
                <PremountedMedia
                  src={staticFile('remotionPartical/particle_3.webm')}
                  type="video"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  muted={true}
                  transparent={true}
                  premountFor={premountFor}
                />
              </AbsoluteFill>
            )}
            {/* CTA Overlay */}
            {ctaData?.templateKey && sectionLength && sectionLength-1 === sectionIndex && (
              <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', zIndex: 10000 }}>
                <CtaOverlay
                  imageSrc={ctaData?.image}
                  secondaryImageSrc={ctaData?.secondaryImage}
                  headline={ctaData?.headline}
                  subheadline={ctaData?.subheadline}
                  variation={ctaData?.templateKey}
                  productAnimation="meetCenter"
                  width={videoWidth}
                  height={videoHeight}
                  ctaText={ctaData?.cta}
                />
              </div>
            )}
            {/* Product Intro Overlay */}
            {/* {productIntroData?.templateKey && sectionLength && sectionIndex === 0 && ( */}
              {/* <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', zIndex: 10000 }}>
                <ProductIntroOverlay
                  imageSrc={'images/broll.jpg'}
                  headline={'For testing'}
                  subheadline={'for testing'}
                  variation={4}
                  productAnimation={'slideLeft'}
                  width={videoWidth}
                  height={videoHeight}
                />
              </div> */}
            {/* )} */}
          </AbsoluteFill>
        )}
      </>
    )
  }
)

SinglePage.displayName = 'SinglePage'